import React, { useState, useEffect, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { formatDate } from '@fullcalendar/core';
import { Clock, MapPin, User, Loader2, ChevronRight, ChevronLeft, Calendar, X } from 'lucide-react';
import type { CalendarEvent } from '../../lib/types';
import { useApp } from '../AppProvider';
import { translations } from '../../lib/i18n/translations';
import { useBreakpoints } from '../../lib/hooks/useBreakpoints';
import { MobileDrawer } from '../ui/MobileDrawer';
import './calendar-styles.css';
import { eventOutcomeLabel, escapeHtml } from '../../lib/calendar/outcomeLabel';

// FullCalendar styles are included in the package

interface CalendarViewProps {
  events: CalendarEvent[];
  onDateClick?: (date: Date) => void;
  onDateSelect?: (start: Date, end: Date, allDay: boolean) => void;
  // Modifiers opzionali (shift/meta) per supportare multi-selezione nei chiamanti che la usano.
  // Retro-compatibile: i chiamanti esistenti continuano a chiamare onEventClick(event).
  onEventClick?: (event: CalendarEvent, modifiers?: { shift: boolean; meta: boolean }) => void;
  // Set opzionale di id evento selezionati: se presente, gli eventi corrispondenti vengono evidenziati.
  selectedEventIds?: Set<string>;
  onEventDrop?: (eventId: string, newStart: Date, newEnd: Date, allDay: boolean) => void;
  onEventResize?: (eventId: string, newStart: Date, newEnd: Date) => void;
  onViewChange?: (start: Date, end: Date, view: string) => void;
  calendarRef?: React.RefObject<any>;
  initialDate?: Date;
  isLoading?: boolean;
  googleLoading?: boolean;
  filterText?: string;
  sidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  // Note: calendarSourcesSidebar props removed - now handled by Layout via CalendarModeContext
  t?: any;
  highlightEventId?: string;
  hideEventId?: string; // ID evento da nascondere (es. evento Google duplicato durante edit)
  // Drop esterno (es. team planning: drag di un progetto/task dalla sidebar → crea work-slot)
  droppable?: boolean;
  onEventReceive?: (info: any) => void;
  // Tasto destro su un evento (es. team planning: menu contestuale sullo slot)
  onEventContextMenu?: (eventId: string, isGoogleEvent: boolean, clientX: number, clientY: number) => void;
  // Override OPZIONALE della vista corrente di FullCalendar (es. Team Planning: Settimana / 2 Settimane / Mese).
  // Quando undefined → comportamento attuale invariato (CalendarPage/CalendarModal non passano questa prop).
  // Valori validi: 'timeGridWeek' | 'timeGridTwoWeeks' | 'dayGridMonth' (o qualsiasi vista registrata).
  viewOverride?: string;
}

export function CalendarView({
  events,
  onDateClick,
  onDateSelect,
  onEventClick,
  onEventDrop,
  onEventResize,
  onViewChange,
  calendarRef,
  initialDate,
  isLoading = false,
  googleLoading = false,
  filterText = '',
  sidebarOpen = true,
  onToggleSidebar,
  t = {},
  highlightEventId,
  hideEventId,
  droppable = false,
  onEventReceive,
  onEventContextMenu,
  selectedEventIds,
  viewOverride
}: CalendarViewProps) {
  // Default view: su mobile la grid 7-day di FullCalendar è inutilizzabile
  // (eventi tagliati, header day overflow). Defaultiamo a 'listWeek' (agenda
  // verticale) che è il pattern standard per calendari mobile.
  // La detection di mobile avviene leggendo window.innerWidth lazy nel useState
  // initializer per evitare il flash di view sbagliata al primo render.
  const [currentView, setCurrentView] = useState<'dayGridMonth' | 'timeGridWeek' | 'timeGridThreeDay' | 'timeGridDay' | 'listWeek'>(
    () => (typeof window !== 'undefined' && window.innerWidth < 768) ? 'timeGridThreeDay' : 'timeGridWeek'
  );
  const [filteredEvents, setFilteredEvents] = useState<CalendarEvent[]>(events);
  
  // Filter events based on search text
  useEffect(() => {
    if (!filterText) {
      setFilteredEvents(events);
      return;
    }

    const searchTerm = filterText.toLowerCase().trim();
    const filtered = events.filter(event => {
      return (
        (event.title && event.title.toLowerCase().includes(searchTerm)) ||
        (event.description && event.description.toLowerCase().includes(searchTerm)) ||
        (event.location && event.location.toLowerCase().includes(searchTerm))
      );
    });

    setFilteredEvents(filtered);
  }, [events, filterText]);

  // Force FullCalendar to resize when sidebar states change
  useEffect(() => {
    if (calendarRef?.current) {
      const calendarApi = calendarRef.current.getApi();
      // Use a small timeout to ensure the DOM has updated
      const timeoutId = setTimeout(() => {
        calendarApi.updateSize();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [sidebarOpen, calendarRef]);

  // NOTE: Removed the problematic useEffect that was causing infinite loop
  // The view state is now managed only through FullCalendar's viewDidMount and datesSet callbacks

  // Ensure proper rendering when the component mounts
  useEffect(() => {
    if (calendarRef?.current) {
      // When component is mounted, force the view to render properly
      const timeoutId = setTimeout(() => {
        if (calendarRef?.current) {
          calendarRef.current.getApi().updateSize();
        }
      }, 250);
      return () => clearTimeout(timeoutId);
    }
  }, [calendarRef]);
  
  // Override vista corrente (es. Team Planning: Settimana / 2 Settimane / Mese).
  // Solo se viewOverride è passata: chiama changeView sull'API FullCalendar.
  // Senza viewOverride questo effect è no-op → comportamento attuale invariato.
  useEffect(() => {
    if (!viewOverride) return;
    const api = calendarRef?.current?.getApi?.();
    if (!api) return;
    if (api.view?.type !== viewOverride) {
      api.changeView(viewOverride);
    }
  }, [viewOverride, calendarRef]);

  // Inizializza la data selezionata al giorno corrente
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate || today);
  const { user } = useApp();
  const { isMobile } = useBreakpoints();

  // Get translations (use prop t if available, otherwise get from translations)
  const translations_t = !t || Object.keys(t).length === 0 ? translations[user?.language || 'it'].calendar : t;

  // Helper function to convert hex to rgba with opacity
  const hexToRgba = (hex: string, opacity: number): string => {
    // Handle rgba colors
    if (hex.startsWith('rgba')) return hex;
    if (hex.startsWith('rgb')) {
      const match = hex.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${opacity})`;
      }
    }
    // Handle hex colors
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${opacity})`;
    }
    return hex;
  };

  // Chiave stabile del set di eventi selezionati: cambia solo quando cambia il contenuto del set,
  // così la memo di calendarEvents si ricomputa e FullCalendar riapplica le classNames di selezione.
  const selectedKey = useMemo(
    () => (selectedEventIds ? Array.from(selectedEventIds).sort().join(',') : ''),
    [selectedEventIds]
  );

  // Convert our custom event format to FullCalendar event format
  // Memoizzato per evitare che FullCalendar faccia revert del drag quando il parent si re-renderizza
  const calendarEvents = useMemo(() => {
    return filteredEvents
      .filter(event => event.id !== hideEventId) // Nasconde evento duplicato (es. Google durante edit CRM)
      .map(event => {
        // Get the base color (full opacity for border)
        const baseColor = event.color || '#3B82F6'; // Blue as fallback
        // Check if this is the pending activity (being created in drawer)
        const isPendingActivity = event.id === 'pending-activity';
        // Create light background with low opacity (Notion style)
        // Pending activity gets higher opacity for visibility
        const backgroundColor = isPendingActivity
          ? hexToRgba(baseColor, 0.35)
          : hexToRgba(baseColor, 0.18);
        // Check if this event should be highlighted
        const isHighlighted = highlightEventId && event.id === highlightEventId;

        // Check if AlfaDocs event is cancelled
        const isAlfadocsCancelled = event.source === 'alfadocs' && (event.metadata as any)?.alfadocs_state === 'cancelled';

        // Determine classNames
        let classNames: string[] = [];
        if (isHighlighted) classNames = ['fc-event-highlighted'];
        else if (isPendingActivity) classNames = ['fc-event-pending-activity'];
        if (isAlfadocsCancelled) classNames.push('fc-event-cancelled');
        // Multi-selezione (es. Team Planning): evidenzia gli eventi selezionati
        if (selectedEventIds?.has(event.id)) classNames.push('fc-event-selected');
        // Calendari condivisi: "solo occupato" e appuntamenti con esito
        const md = (event.metadata || {}) as Record<string, any>;
        if (md.busy_only) classNames.push('fc-event-busy');
        if (md.done) classNames.push('fc-event-done');
        if (event.source === 'google' || event.source === 'microsoft') classNames.push('fc-event-outline');

        return {
          id: event.id,
          title: isAlfadocsCancelled ? `[Cancellato] ${event.title}` : event.title,
          start: event.start_time,
          end: event.end_time,
          allDay: event.all_day,
          extendedProps: {
            // Passthrough di eventuali extendedProps custom (es. team planning: assignedTo, taskNames, userColor, isGoogleEvent).
            // Spread per primo così i campi noti sotto mantengono la priorità.
            ...(event.extendedProps || {}),
            description: event.description,
            location: event.location,
            source: event.source,
            source_name: event.source_name,
            is_editable: event.is_editable,
            event_type: event.event_type,
            metadata: event.metadata,
            baseColor: baseColor // Store original color for border
          },
          backgroundColor: isAlfadocsCancelled ? hexToRgba('#9CA3AF', 0.15) : isHighlighted ? hexToRgba('#3B82F6', 0.25) : backgroundColor,
          borderColor: isAlfadocsCancelled ? '#9CA3AF' : isHighlighted ? '#3B82F6' : baseColor,
          textColor: isAlfadocsCancelled ? '#9CA3AF' : isPendingActivity ? '#fff' : '#1a1a1a',
          // When highlightEventId is set (edit mode), only the highlighted event is editable
          editable: highlightEventId ? isHighlighted : (event.is_editable !== false),
          classNames
        };
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEvents, highlightEventId, hideEventId, selectedKey]);

  // Handle date click
  const handleDateClick = (arg: any) => {
    setSelectedDate(arg.date);
    if (onDateClick) {
      onDateClick(arg.date);
    }
  };

  const handleDateSelect = (arg: any) => {
    if (onDateSelect) {
      // Determine if it's an all-day event based on the selection
      const allDay = !arg.start.getTime || arg.allDay;
      onDateSelect(arg.start, arg.end, allDay);
    }
  };
  
  // Seleziona automaticamente il giorno corrente al caricamento
  useEffect(() => {
    // Al caricamento del componente, seleziona il giorno corrente
    if (onDateClick) {
      onDateClick(today);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle event click
  const handleEventClick = (arg: any) => {
    const eventId = arg.event.id;
    const clickedEvent = events.find(e => e.id === eventId);

    if (clickedEvent && onEventClick) {
      const je: MouseEvent | undefined = arg.jsEvent;
      const modifiers = {
        shift: !!je?.shiftKey,
        meta: !!(je?.metaKey || je?.ctrlKey)
      };
      onEventClick(clickedEvent, modifiers);
    }
  };
  
  // Handle event drop (drag & drop)
  const handleEventDrop = (info: any) => {
    if (!onEventDrop) return;

    const eventId = info.event.id;
    const newStart = info.event.start;
    const newEnd = info.event.end || new Date(newStart.getTime() + 3600000); // Default to 1 hour if no end time
    const allDay = info.event.allDay;

    // Se highlightEventId è definito (edit mode), solo quell'evento può essere spostato
    if (highlightEventId && eventId !== highlightEventId) {
      info.revert();
      return;
    }

    // Check if the event is editable (for normal mode)
    const event = events.find(e => e.id === eventId);
    if (!event || event.is_editable === false) {
      info.revert(); // Revert the drag if not editable
      return;
    }

    onEventDrop(eventId, newStart, newEnd, allDay);
  };
  
  // Handle event resize
  const handleEventResize = (info: any) => {
    if (!onEventResize) return;

    const eventId = info.event.id;
    const newStart = info.event.start;
    const newEnd = info.event.end;

    // Se highlightEventId è definito (edit mode), solo quell'evento può essere ridimensionato
    if (highlightEventId && eventId !== highlightEventId) {
      info.revert();
      return;
    }

    // Check if the event is editable (for normal mode)
    const event = events.find(e => e.id === eventId);
    if (!event || event.is_editable === false) {
      info.revert(); // Revert the resize if not editable
      return;
    }

    onEventResize(eventId, newStart, newEnd);
  };

  // Filter events for the selected date (for the sidebar)
  const eventsForSelectedDate = filteredEvents.filter(event => {
    const eventDate = new Date(event.start_time);
    return eventDate.toDateString() === selectedDate.toDateString();
  });

  // Contenuto sidebar eventi (riusato sia in desktop che in MobileDrawer)
  const sidebarContent = (
    <div className={`${isMobile ? '' : 'p-4'} h-full flex flex-col`}>
      <h3 className="font-semibold text-black/90 dark:text-white/90 mb-4 text-base flex-shrink-0">
        {selectedDate.toLocaleDateString('it-IT', {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        }).replace(/^./, (str) => str.toUpperCase())}
      </h3>

      {/* Group events by calendar source */}
      <div className="space-y-3 overflow-y-auto flex-1">
        {eventsForSelectedDate.length > 0 ? (
          Object.entries(
            eventsForSelectedDate.reduce((groups, event) => {
              const sourceKey = event.source === 'google'
                ? `google-${event.source_id || 'unknown'}`
                : 'crm';
              if (!groups[sourceKey]) {
                groups[sourceKey] = {
                  events: [],
                  name: event.source_name || (event.source === 'google' ? translations_t.googleCalendar : translations_t.crmCalendar),
                  color: event.color || (event.source === 'google' ? '#4285F4' : '#000000'),
                  source: event.source
                };
              }
              groups[sourceKey].events.push(event);
              return groups;
            }, {} as Record<string, { events: CalendarEvent[], name: string, color: string, source?: string }>)
          ).map(([sourceKey, group]) => (
            <div key={sourceKey} className="mb-4">
              <div className="flex items-center mb-2">
                <span
                  className="w-2.5 h-2.5 rounded-full mr-2"
                  style={{ backgroundColor: group.color }}
                />
                <span className="text-xs font-medium text-black/80 dark:text-white/80">{group.name}</span>
              </div>
              <div className="space-y-1.5 pl-4">
                {group.events.map((event) => (
                  <div
                    key={event.id}
                    className="p-2 border-l-3 rounded-r-md hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer transition-colors duration-150"
                    style={{ borderLeftColor: event.color || group.color }}
                    onClick={() => onEventClick && onEventClick(event)}
                  >
                    <h4 className="font-medium text-black/90 dark:text-white/90 text-xs">{event.title}</h4>
                    {event.description && (
                      <p className="text-xs text-black/60 dark:text-white/60 mt-0.5 line-clamp-1">{event.description}</p>
                    )}
                    <div className="flex items-center mt-1 text-xs text-black/60 dark:text-white/60">
                      <Clock className="h-2.5 w-2.5 mr-1 flex-shrink-0" />
                      <span>
                        {event.all_day
                          ? t.allDayEvent
                          : `${new Date(event.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(event.end_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`}
                      </span>
                    </div>
                    {event.location && (
                      <div className="flex items-center mt-0.5 text-xs text-black/60 dark:text-white/60">
                        <MapPin className="h-2.5 w-2.5 mr-1 flex-shrink-0" />
                        <span className="truncate">{event.location}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-6 flex-shrink-0">
            <div className="text-black/40 dark:text-white/40 mb-2">
              <Calendar className="h-8 w-8 mx-auto" />
            </div>
            <p className="text-xs text-black/60 dark:text-white/60">{t.noEvents}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-full relative">
      {/* Main Calendar Area */}
      <div
        className={`flex-1 transition-all duration-300 ${
          sidebarOpen && !isMobile ? 'sm:pr-96' : ''
        }`}
        style={{ minHeight: 'calc(100vh - 200px)' }}
      >

        <div className="h-full overflow-hidden relative">
          {(isLoading || googleLoading) && (
            <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 z-10 flex items-center justify-center pointer-events-none">
              <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                <span className="text-sm text-gray-600 dark:text-gray-300">Caricamento eventi...</span>
              </div>
            </div>
          )}
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            initialView={viewOverride || (isMobile ? 'timeGridThreeDay' : 'timeGridWeek')}
            headerToolbar={false} // We're using custom header buttons
            allDaySlot={false} // Nasconde la riga "all-day" (richiesta mobile, vale anche desktop)
            noEventsContent="Nessun appuntamento in questo periodo" // Vista agenda: testo IT (no inglese)
            views={{
              dayGridMonth: { // name of view
                titleFormat: { year: 'numeric', month: 'long' }
              },
              timeGridWeek: {
                titleFormat: { year: 'numeric', month: 'short', day: '2-digit' },
                dayHeaderFormat: { weekday: 'short', day: '2-digit' }
              },
              timeGridThreeDay: {
                type: 'timeGrid',
                duration: { days: 3 },
                titleFormat: { year: 'numeric', month: 'short', day: '2-digit' },
                dayHeaderFormat: { weekday: 'short', day: '2-digit' }
              },
              // Vista custom "2 settimane" (Team Planning). time-grid su 14 giorni:
              // mantiene drag/resize/select/context-menu identici alla timeGridWeek.
              timeGridTwoWeeks: {
                type: 'timeGrid',
                duration: { weeks: 2 },
                buttonText: '2 settimane',
                titleFormat: { year: 'numeric', month: 'short', day: '2-digit' },
                dayHeaderFormat: { weekday: 'short', day: '2-digit' }
              },
              timeGridDay: {
                titleFormat: { year: 'numeric', month: 'long', day: '2-digit' }
              },
              listWeek: {
                titleFormat: { year: 'numeric', month: 'long' }
              }
            }}
            buttonText={{
              today: 'Today',
              month: translations_t.monthView,
              week: translations_t.weekView,
              day: translations_t.dayView,
              list: translations_t.listView
            }}
            events={calendarEvents}
            dateClick={handleDateClick}
            select={handleDateSelect}
            dayCellClassNames={(arg) => {
              // Highlight today's date with a special class
              return arg.date.toDateString() === today.toDateString() ? 'fc-day-today' : '';
            }}
            eventClick={handleEventClick}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: false
            }}
            eventContent={(arg) => {
              // Custom event content with intelligent time placement
              const timeText = arg.timeText;
              const title = arg.event.title;
              const isBooking = arg.event.extendedProps?.event_type === 'booking';
              const bookingStatus = arg.event.extendedProps?.metadata?.status;
              const statusColors: Record<string, string> = { confirmed: '#10B981', pending: '#F59E0B', cancelled: '#EF4444', completed: '#3B82F6', no_show: '#6B7280', rescheduled: '#8B5CF6' };
              const badgeColor = statusColors[bookingStatus as string] || '#F59E0B';
              // Pallino del tipo (o della persona, se il colore è per tipo) sulle task commerciali
              const typeDot = arg.event.extendedProps?.metadata?.type_dot;
              const dotColor = isBooking ? badgeColor : (typeof typeDot === 'string' && /^#[0-9a-fA-F]{6}$/.test(typeDot) ? typeDot : null);
              const bookingBadge = dotColor
                ? `<span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:${dotColor};margin-right:4px;vertical-align:middle;"></span>`
                : '';
              // Task commerciale chiusa con esito: etichetta breve (es. "Assente") prima del titolo.
              // inline-block: il barrato delle task chiuse non la attraversa.
              const outcomeLbl = eventOutcomeLabel(arg.event.extendedProps?.metadata);
              const outcomeBadge = outcomeLbl
                ? `<span data-event-outcome style="display:inline-block;text-decoration:none;font-size:10px;font-weight:600;line-height:1.35;padding:0 4px;border-radius:3px;background:rgba(15,123,108,0.16);color:#0B5E52;margin-right:4px;vertical-align:middle;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(outcomeLbl)}</span>`
                : '';

              // Calculate event duration in minutes
              const start = arg.event.start;
              const end = arg.event.end;
              let durationMinutes = 60; // default duration

              if (start && end) {
                durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
              }

              // For short events (30 minutes or less), show time inline
              if (durationMinutes <= 30) {
                const inlineText = timeText ? `${title} ${timeText}` : title;

                return {
                  html: `
                    <div style="padding: 2px 4px; overflow: hidden; height: 100%; display: flex; align-items: center;">
                      <div style="font-size: 13px; font-weight: 500; color: #000; line-height: 1.1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                        ${bookingBadge}${outcomeBadge}${inlineText}
                      </div>
                    </div>
                  `
                };
              } else {
                // For longer events, show title and time on separate lines
                return {
                  html: `
                    <div style="padding: 2px 4px; overflow: hidden; height: 100%;">
                      <div style="font-size: 13px; font-weight: 500; color: #000; line-height: 1.2; margin-bottom: 1px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                        ${bookingBadge}${outcomeBadge}${title}
                      </div>
                      ${timeText ? `<div style="font-size: 9px; color: #1a1a1a; line-height: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                        ${timeText}
                      </div>` : ''}
                    </div>
                  `
                };
              }
            }}
            // Enable drag-and-drop functionality
            editable={true}
            droppable={droppable}
            eventReceive={onEventReceive}
            eventDrop={handleEventDrop}
            eventResize={handleEventResize}
            eventResizableFromStart={true}
            height="100%"
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            // Eventi sovrapposti affiancati in colonne (stile Google Calendar)
            // invece dello stacking con copertura parziale (default FullCalendar)
            slotEventOverlap={false}
            weekends={true}
            firstDay={1} // Monday (European week)
            locale="it" // Use Italian locale for date formatting
            initialDate={initialDate || today} // Validated requested day or the normal default
            nowIndicator={true} // Show "now" indicator in day and week views
            scrollTime="08:00:00" // Scroll to 8am by default
            dayHeaderContent={(args) => {
              // Custom day header formatting for week view: "Lun 02" instead of "Lun 02/09"
              if (args.view.type === 'timeGridWeek') {
                const date = args.date;
                const dayName = date.toLocaleDateString('it-IT', { weekday: 'short' });
                const dayNumber = date.getDate().toString().padStart(2, '0');
                return `${dayName.toUpperCase()} ${dayNumber}`;
              }
              return args.text; // Use default formatting for other views
            }}
            viewDidMount={(arg) => {
              setCurrentView(arg.view.type as any);
              // Pass the updated view type to parent component if callback exists
              if (onViewChange) {
                onViewChange(arg.view.currentStart, arg.view.currentEnd, arg.view.type);
              }
            }}
            datesSet={(dateInfo) => {
              // Only update the view type without triggering additional API calls
              const viewType = dateInfo.view.type;
              if (viewType !== currentView) {
                setCurrentView(viewType as any);
              }

              // We've completely separated the view change from the data fetch to avoid loops
              // API calls will only happen when explicitly requested via navigation buttons
            }}
            eventDidMount={(info) => {
              // Add class to overlapping events (events that don't take full width)
              const eventEl = info.el;

              // Menu contestuale (tasto destro) sull'evento, se il parent fornisce l'handler
              if (onEventContextMenu) {
                eventEl.addEventListener('contextmenu', (e: MouseEvent) => {
                  e.preventDefault();
                  onEventContextMenu(
                    info.event.id,
                    !!info.event.extendedProps?.isGoogleEvent,
                    e.clientX,
                    e.clientY
                  );
                });
              }
              const harness = eventEl.closest('.fc-timegrid-event-harness') as HTMLElement;

              if (harness) {
                const style = harness.getAttribute('style');

                // Check multiple indicators of overlapping:
                // 1. Check inline style for left/right positioning
                // 2. Check computed width (overlapping events will have width < 100%)
                let isOverlapping = false;

                if (style) {
                  // More robust check: look for any left or right value that's not 0% or 100%
                  const leftMatch = style.match(/left:\s*([0-9.]+)%/);
                  const rightMatch = style.match(/right:\s*([0-9.]+)%/);
                  const widthMatch = style.match(/width:\s*([0-9.]+)%/);

                  const hasNonZeroLeft = leftMatch && parseFloat(leftMatch[1]) > 0;
                  const hasNonZeroRight = rightMatch && parseFloat(rightMatch[1]) > 0;
                  const hasReducedWidth = widthMatch && parseFloat(widthMatch[1]) < 100;

                  isOverlapping = !!(hasNonZeroLeft || hasNonZeroRight || hasReducedWidth);
                }

                // Alternative check: if harness has siblings at the same level, it's overlapping
                if (!isOverlapping) {
                  const parent = harness.parentElement;
                  if (parent) {
                    const siblings = Array.from(parent.children).filter(
                      child => child.classList.contains('fc-timegrid-event-harness')
                    );
                    // If there are multiple harnesses in the same parent container, they overlap
                    if (siblings.length > 1) {
                      isOverlapping = true;
                    }
                  }
                }

                if (isOverlapping) {
                  eventEl.classList.add('fc-event-overlapping');
                }
              }

              // Force calendar to update size when sidebars change
              setTimeout(() => {
                if (calendarRef?.current) {
                  calendarRef.current.getApi().updateSize();
                }
              }, 100);
            }}
          />
        </div>
      </div>

      {/* Note: Calendar Sources Sidebar removed - now handled by Layout via CalendarModeContext */}

      {/* Events Sidebar - Desktop: absolute panel, Mobile: MobileDrawer */}
      {isMobile ? (
        <MobileDrawer
          isOpen={sidebarOpen}
          onClose={() => onToggleSidebar?.()}
          title={selectedDate.toLocaleDateString('it-IT', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          }).replace(/^./, (str) => str.toUpperCase())}
          height="half"
          showHandle
          swipeToClose
        >
          {sidebarContent}
        </MobileDrawer>
      ) : (
        sidebarOpen && (
          <div
            className="absolute right-0 top-0 w-80 glass-card rounded-xl shadow-lg transition-all duration-300 z-20"
            style={{ height: '100%' }}
          >
            <div className={`h-full overflow-hidden ${sidebarOpen ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
              {sidebarContent}
            </div>
          </div>
        )
      )}
    </div>
  );
}